import { SREDReportRow } from 'api/sred';
import { PivotProject, PivotCase } from '../types';

function parseProjectNum(sredProject: string): number {
  const parts = sredProject.split('-');
  return parseInt(parts[parts.length - 1], 10) || 0;
}

export function formatProjectLabel(keyword: string): string {
  const match = keyword.match(/^SRED\s+(\d{4})\s+PRJ-(\d+)$/);
  if (match) return `Project ${match[2]}`;
  return keyword;
}

export function getActiveConsultants(rows: SREDReportRow[]): string[] {
  const active = new Set<string>();
  for (const row of rows) {
    if ((row.reg_hrs > 0 || row.ot_hrs > 0) && row.consultant) {
      active.add(row.consultant);
    }
  }
  return Array.from(active).sort();
}

export function buildPivotData(rows: SREDReportRow[]): PivotProject[] {
  const projectMap = new Map<number, { keyword: string; cases: Map<number, { title: string; consultants: Map<string, { reg: number; ot: number }> }> }>();

  for (const row of rows) {
    const prjNum = parseProjectNum(row.sred_project);
    if (!projectMap.has(prjNum)) {
      projectMap.set(prjNum, { keyword: row.sred_project, cases: new Map() });
    }
    const project = projectMap.get(prjNum)!;
    if (!project.cases.has(row.case_id)) {
      project.cases.set(row.case_id, { title: row.case_title, consultants: new Map() });
    }
    const caseData = project.cases.get(row.case_id)!;
    const existing = caseData.consultants.get(row.consultant) || { reg: 0, ot: 0 };
    caseData.consultants.set(row.consultant, {
      reg: existing.reg + (row.reg_hrs || 0),
      ot: existing.ot + (row.ot_hrs || 0),
    });
  }

  const activeConsultants = getActiveConsultants(rows);
  const projects: PivotProject[] = [];

  const sortedNums = Array.from(projectMap.keys()).sort((a, b) => a - b);

  for (const prjNum of sortedNums) {
    const { keyword, cases: casesMap } = projectMap.get(prjNum)!;

    const prjTotals: Record<string, { reg: number; ot: number }> = {};
    for (const c of activeConsultants) prjTotals[c] = { reg: 0, ot: 0 };
    let prjTotalHrs = 0;

    const pivotCases: PivotCase[] = [];
    const sortedCaseIds = Array.from(casesMap.keys()).sort((a, b) => a - b);

    for (const caseId of sortedCaseIds) {
      const caseData = casesMap.get(caseId)!;
      const consultants: Record<string, { reg: number; ot: number }> = {};
      let caseTotalHrs = 0;

      for (const con of activeConsultants) {
        const cd = caseData.consultants.get(con) || { reg: 0, ot: 0 };
        consultants[con] = { reg: cd.reg, ot: cd.ot };
        prjTotals[con].reg += cd.reg;
        prjTotals[con].ot += cd.ot;
        caseTotalHrs += cd.reg + cd.ot;
      }

      prjTotalHrs += caseTotalHrs;

      pivotCases.push({
        caseId,
        caseTitle: caseData.title,
        consultants,
        totalHrs: Math.round(caseTotalHrs * 100) / 100,
      });
    }

    projects.push({
      projectNum: prjNum,
      projectTitle: keyword,
      cases: pivotCases,
      totals: prjTotals,
      totalHrs: Math.round(prjTotalHrs * 100) / 100,
    });
  }

  return projects;
}
